import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

interface GrowthIndicatorSliderProps {
  min?: number;
  max?: number;
  value: [number, number];
  onChange: (value: [number, number]) => void;
  disabled?: boolean;
}

const GrowthIndicatorSlider: React.FC<GrowthIndicatorSliderProps> = ({
  min = -20,
  max = 120,
  value,
  onChange,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [minValue, setMinValue] = useState(value[0]);
  const [maxValue, setMaxValue] = useState(value[1]);
  const sliderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMinValue(value[0]);
    setMaxValue(value[1]);
  }, [value]);

  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const newMin = Math.min(Number(e.target.value), maxValue - 1);
    setMinValue(newMin);
    onChange([newMin, maxValue]);
  };

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const newMax = Math.max(Number(e.target.value), minValue + 1);
    setMaxValue(newMax);
    onChange([minValue, newMax]);
  };

  const getBackgroundStyle = () => {
    const minPercent = ((minValue - min) / (max - min)) * 100;
    const maxPercent = ((maxValue - min) / (max - min)) * 100;

    return {
      background: `linear-gradient(to right, 
        #ef4444 0%, 
        #ef4444 ${minPercent}%, 
        #eab308 ${minPercent}%, 
        #eab308 ${maxPercent}%, 
        #22c55e ${maxPercent}%, 
        #22c55e 100%)`,
    };
  };

  const rangeInputClassName = `absolute w-full h-2 appearance-none bg-transparent pointer-events-none disabled:cursor-not-allowed
  [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-gray-400
  [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-gray-400
  [&::-webkit-slider-runnable-track]:pointer-events-none [&::-moz-range-track]:pointer-events-none
  ${disabled ? 
    "[&::-webkit-slider-thumb]:cursor-not-allowed [&::-moz-range-thumb]:cursor-not-allowed [&::-webkit-slider-thumb]:pointer-events-none [&::-moz-range-thumb]:pointer-events-none" : 
    "[&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:cursor-pointer [&::-webkit-slider-thumb]:pointer-events-auto [&::-moz-range-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:shadow-md [&::-moz-range-thumb]:shadow-md enabled:hover:[&::-webkit-slider-thumb]:border-gray-500 enabled:hover:[&::-moz-range-thumb]:border-gray-500"
  }`;

  return (
    <div className={`w-full space-y-4 ${disabled ? 'opacity-70' : ''}`}>
      <div className="relative pt-6 pb-2" ref={sliderRef}>
        {/* Track */}
        <div
          className={`absolute h-2 w-full rounded-full ${disabled ? 'cursor-not-allowed' : ''}`}
          style={getBackgroundStyle()}
        />

        {/* Min Value Input */}
        <input
          type="range"
          min={min}
          max={max}
          value={minValue}
          onChange={handleMinChange}
          disabled={disabled}
          className={rangeInputClassName}
          style={{ zIndex: minValue > maxValue - 10 ? 5 : 3 }}
        />

        {/* Max Value Input */}
        <input
          type="range"
          min={min}
          max={max}
          value={maxValue}
          onChange={handleMaxChange}
          disabled={disabled}
          className={rangeInputClassName}
          style={{ zIndex: 4 }}
        />

        {/* Value Labels */}
        <div className="absolute -top-1 left-0 right-0 flex justify-between text-xs font-medium text-gray-600">
          <span
            className="absolute bg-white px-2 py-1 rounded shadow-sm border border-gray-200"
            style={{
              left: `calc(${((minValue - min) / (max - min)) * 100}% + ${10 - ((minValue - min) / (max - min)) * 20}px)`,
              transform: "translateX(-50%)",
            }}
          >
            {minValue}
          </span>
          <span
            className="absolute bg-white px-2 py-1 rounded shadow-sm border border-gray-200"
            style={{
              left: `calc(${((maxValue - min) / (max - min)) * 100}% + ${10 - ((maxValue - min) / (max - min)) * 20}px)`,
              transform: "translateX(-50%)",
            }}
          >
            {maxValue}
          </span>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-between text-xs text-gray-600">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-red-500" />
          <span>{t("settings.growthIndicator.declining")} (&lt; {minValue})</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-yellow-500" />
          <span>{t("settings.growthIndicator.stable")} ({minValue} - {maxValue})</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-green-500" />
          <span>{t("settings.growthIndicator.growth")} (&gt; {maxValue})</span>
        </div>
      </div>
    </div>
  );
};

export default GrowthIndicatorSlider;